import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  fetchMailingList,
  useArchiveMailingList,
  useMailingListPreview,
  useMailingListSendEngine,
  useMailingListSendAll,
  useMailingListSendStatus,
  useMailingListTestSend,
  usePatchMailingList,
  useResolveMailingList,
  type MailingListPatchInput,
  type MailingListRecipientDto,
} from '../../api/mailing-lists';
import { Badge, Button, EmptyState, ErrorState, Field, Input, Modal, Pagination, Select, Spinner, Textarea } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { MailingListForm } from './MailingListForm';
import {
  SEND_UNAVAILABLE_HINT,
  STATE_TABS,
  STATUS_LABEL,
  STATUS_TONE,
  channelLabel,
  channelOf,
  clickTiles,
  countTiles,
  deliveryTiles,
  formatListRunProgress,
  formatResolveReport,
  formatSendAllNotice,
  formatTemplatePin,
  formatWhen,
  isArchived,
  renderReadyLabel,
  selectionModeLabel,
  describeSelection,
  stateTabCount,
  trackLabel,
  type ListStateFilter,
} from './lists-model';

/**
 * ONE mailing list (#34.3–#34.7, T.Z132): the frozen facts in a header card,
 * the counts as stat tiles, the recipients in the register's own table chrome
 * behind `?state=` tabs, and the three write verbs — «Обновить состав»
 * (#34.6), архивировать/вернуть (#34.5) and the edit fold (#34.4, which
 * re-resolves and reports the diff like every other write).
 *
 * THE SEND: the engine is the sibling track T.Z133. Until its routes answer,
 * the two buttons sit here visible but disabled with an honest hint — the
 * probe is GET-only (`useMailingListSendEngine`), so detecting the engine can
 * never send a letter. When the routes exist the same two buttons wire to
 * them: a test letter to the address the OPERATOR typed, and a confirmed
 * paced run over the LIVE recipients. Under the send panel sits THIS list's
 * own status read (#34.10, T.Z185): the five delivery chips and the live run's
 * progress line — the fixed panels' grammar, scoped by the list.
 */

export const LIST_PAGE_SIZE = 20;

interface MailingListViewProps {
  listId: number;
  state: ListStateFilter;
  page: number;
  onNavigate: (next: { state?: ListStateFilter; page?: number }) => void;
  onClose: () => void;
}

export function MailingListView({ listId, state, page, onNavigate, onClose }: MailingListViewProps) {
  const list = useQuery({
    queryKey: ['mailing-lists', 'page', listId, state, page],
    queryFn: ({ signal }) => fetchMailingList(listId, { state, page, pageSize: LIST_PAGE_SIZE }, signal),
  });

  const archived = list.data ? isArchived(list.data.list) : false;
  const resolve = useResolveMailingList();
  const archive = useArchiveMailingList();
  const patch = usePatchMailingList();
  const engine = useMailingListSendEngine(listId);

  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const counts = list.data?.counts ?? null;
  const rows = list.data?.data ?? [];
  const meta = list.data?.meta ?? { page, pageSize: LIST_PAGE_SIZE, total: 0 };

  const runResolve = () => {
    setNotice(null);
    resolve.mutate(
      { listId },
      {
        onSuccess: (answer) => setNotice(formatResolveReport(answer.data.resolve)),
        onError: (error) => setNotice(`Пересчёт не прошёл: ${error.message}`),
      },
    );
  };

  return (
    <div className="mailings-list-view" data-testid="mailing-list-view">
      <div className="mailings-list-toolbar">
        <Button onClick={onClose} data-testid="mailing-list-back">
          ← Ко всем спискам
        </Button>
        <span className="mailings-list-toolbar-actions">
          <Button onClick={runResolve} disabled={archived || resolve.isPending} data-testid="mailing-list-resolve">
            {resolve.isPending ? 'Пересчитываем…' : 'Обновить состав'}
          </Button>
          <Button onClick={() => setConfirmArchive(true)} disabled={archive.isPending} data-testid="mailing-list-archive-toggle">
            {archived ? 'Разархивировать' : 'Архивировать'}
          </Button>
          <Button onClick={() => setEditing((prev) => !prev)} disabled={archived} data-testid="mailing-list-edit-toggle">
            {editing ? 'Свернуть правку' : 'Изменить'}
          </Button>
        </span>
      </div>

      {list.isPending ? (
        <Spinner label="Загружаем список" />
      ) : list.isError ? (
        <ErrorState message={String(list.error)} onRetry={() => void list.refetch()} />
      ) : (
        <>
          <HeaderCard
            name={list.data.list.name}
            channel={channelOf(list.data.list)}
            mailingType={list.data.list.mailingType}
            fromEmail={list.data.list.fromEmail}
            template={formatTemplatePin(list.data.list.template)}
            selection={`${selectionModeLabel(list.data.list.selectionMode)} · ${describeSelection(
              list.data.list.selectionMode,
              list.data.list.selectionPayload,
            )}`}
            lastResolvedAt={list.data.list.lastResolvedAt}
            archived={archived}
          />

          <section className="mailings-summary" data-testid="mailing-list-counts">
            {countTiles(counts).map((tile) => (
              <div
                key={tile.key}
                className={`mailings-chip mailings-chip--${tile.tone}`}
                data-testid={`mailing-list-count-${tile.key}`}
                title={tile.hint}
              >
                <span className="mailings-chip-value">{tile.value}</span>
                <span className="mailings-chip-label">{tile.label}</span>
              </div>
            ))}
          </section>

          <SendPanel
            listId={listId}
            channel={channelOf(list.data.list)}
            archived={archived}
            sendable={counts?.renderReady ?? 0}
            engineAvailable={engine.data?.available ?? false}
            candidates={(list.data?.data ?? []).filter((row) => row.removedAt === null)}
            onNotice={setNotice}
          />

          <DeliveryStatusPanel listId={listId} liveTotal={counts?.total ?? 0} channel={channelOf(list.data.list)} />

          {editing ? (
            <section className="card" data-testid="mailing-list-edit">
              <div className="mailings-filters">
                <h2>Изменить список</h2>
              </div>
              <div className="mailings-list-form-wrap">
                <MailingListForm
                  mode="edit"
                  initial={list.data.list}
                  pending={patch.isPending}
                  error={patch.error?.message ?? null}
                  submitLabel="Сохранить и пересчитать"
                  onCancel={() => setEditing(false)}
                  onSubmit={(input) =>
                    patch.mutate({ listId, ...(input as MailingListPatchInput) }, {
                      onSuccess: (answer) => {
                        setEditing(false);
                        setNotice(formatResolveReport(answer.data.resolve));
                      },
                    })
                  }
                />
              </div>
            </section>
          ) : null}

          {notice ? (
            <p className="mailings-sendall-notice" data-testid="mailing-list-notice">
              {notice}
            </p>
          ) : null}

          <section className="card card--flush" data-testid="mailing-list-recipients-card">
            <div className="mailings-filters">
              <h2>Получатели</h2>
              <div className="mailings-state-tabs" role="tablist" aria-label="Какие получатели">
                {STATE_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={state === tab.value}
                    className={`mailings-state-tab${state === tab.value ? ' is-active' : ''}`}
                    onClick={() => onNavigate({ state: tab.value, page: 1 })}
                    data-testid={`mailing-list-state-${tab.value}`}
                  >
                    {tab.label} ({stateTabCount(tab.value, counts)})
                  </button>
                ))}
              </div>
            </div>

            {rows.length === 0 ? (
              <EmptyState
                title="Никого"
                hint={
                  state === 'active'
                    ? 'Под отбор не попал ни один ребёнок — или все выбыли при последнем пересчёте.'
                    : 'В этом срезе строк нет.'
                }
              />
            ) : (
              <RecipientsTable
                listId={listId}
                sms={channelOf(list.data.list) === 'SMS'}
                rows={rows}
                page={meta.page}
                total={meta.total}
                onPage={(next) => onNavigate({ page: next })}
              />
            )}
          </section>
        </>
      )}

      <Modal
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        title={archived ? 'Вернуть список в работу?' : 'Архивировать список?'}
        description={
          archived
            ? 'Список снова можно менять и пересчитывать (восстановление состав не пересчитывает).'
            : 'Архивный список закрыт для правок, пересчётов и отправок, но остаётся в истории — удалить список нельзя.'
        }
        footer={
          <>
            <Button onClick={() => setConfirmArchive(false)}>Отмена</Button>
            <Button
              variant={archived ? 'primary' : 'danger'}
              loading={archive.isPending}
              data-testid="mailing-list-archive-confirm"
              onClick={() =>
                archive.mutate(
                  { listId, archived: !archived },
                  {
                    onSuccess: () => {
                      setConfirmArchive(false);
                      setNotice(archived ? 'Список возвращён в работу.' : 'Список в архиве — правки и отправки закрыты.');
                    },
                    onError: (error) => {
                      setConfirmArchive(false);
                      setNotice(`Не получилось: ${error.message}`);
                    },
                  },
                )
              }
            >
              {archived ? 'Разархивировать' : 'Архивировать'}
            </Button>
          </>
        }
      >
        <p>Название: {list.data?.list.name ?? '—'}</p>
      </Modal>
    </div>
  );
}

function HeaderCard({
  name,
  channel,
  mailingType,
  fromEmail,
  template,
  selection,
  lastResolvedAt,
  archived,
}: {
  name: string;
  channel: ReturnType<typeof channelOf>;
  mailingType: string;
  fromEmail: string;
  template: string;
  selection: string;
  lastResolvedAt: string | null;
  archived: boolean;
}) {
  const sms = channel === 'SMS';
  return (
    <section className="card mailings-list-head" data-testid="mailing-list-head">
      <div className="mailings-list-head-title">
        <h2>{name}</h2>
        <Badge tone={sms ? 'brand' : 'neutral'} testId="mailing-list-channel-badge">
          {channelLabel(channel)}
        </Badge>
        {archived ? <Badge tone="neutral">В архиве</Badge> : null}
      </div>
      <dl className="mailings-list-facts" data-testid="mailing-list-facts">
        <div>
          <dt>Тип</dt>
          <dd>{mailingType}</dd>
        </div>
        <div>
          <dt>{sms ? 'Отправитель' : 'От кого'}</dt>
          <dd>{sms ? `${fromEmail} (подпись SMS задаёт окружение)` : fromEmail}</dd>
        </div>
        <div>
          <dt>Шаблон</dt>
          <dd>{template}</dd>
        </div>
        <div>
          <dt>Отбор</dt>
          <dd>{selection}</dd>
        </div>
        <div>
          <dt>Состав обновлён</dt>
          <dd>{formatWhen(lastResolvedAt)}</dd>
        </div>
      </dl>
    </section>
  );
}

function SendPanel({
  listId,
  channel,
  archived,
  sendable,
  engineAvailable,
  candidates,
  onNotice,
}: {
  listId: number;
  channel: ReturnType<typeof channelOf>;
  archived: boolean;
  sendable: number;
  engineAvailable: boolean;
  /** The LIVE rows of the current page — the test letter carries one of their renders. */
  candidates: MailingListRecipientDto[];
  onNotice: (message: string) => void;
}) {
  const sms = channel === 'SMS';
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [applicationId, setApplicationId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const testSend = useMailingListTestSend();
  const sendAll = useMailingListSendAll();

  const locked = archived || !engineAvailable;
  // Default: the first render-ready row of the page (the letter a test carries),
  // else the first live row — never a row a letter cannot be built from.
  const defaultCandidate = candidates.find((row) => row.renderReady) ?? candidates[0] ?? null;
  const selected = candidates.find((row) => row.applicationId === applicationId) ?? defaultCandidate;

  return (
    <section className="card mailings-send-panel" data-testid="mailing-list-send-panel">
      <div className="mailings-filters">
        <h2>Отправка</h2>
        <p className="mailings-lists-hint">
          {engineAvailable
            ? 'Тест уходит на указанный вами адрес (получателю он не пишется). «Отправить всем» идёт в заданном темпе и продолжается с того места, где остановился.'
            : SEND_UNAVAILABLE_HINT}
        </p>
        <div className="mailings-list-form-grid mailings-send-panel-actions">
          <Field label={sms ? 'Сообщение чьими данными' : 'Письмо чьими данными'} htmlFor="mailing-list-test-child">
            <Select
              id="mailing-list-test-child"
              value={selected ? String(selected.applicationId) : ''}
              disabled={locked}
              onChange={(event) => setApplicationId(Number(event.target.value))}
              data-testid="mailing-list-test-child"
            >
              {candidates.map((row) => (
                <option key={row.applicationId} value={row.applicationId}>
                  {row.fullName ?? '(без ФИО)'} — {sms ? row.phone ?? 'нет телефона' : row.email ?? 'нет адреса'}
                </option>
              ))}
            </Select>
          </Field>
          {sms ? (
            <Field label="Тест-SMS на номер" htmlFor="mailing-list-test-phone">
              <Input
                id="mailing-list-test-phone"
                type="tel"
                value={phone}
                placeholder="+7…"
                disabled={locked}
                onChange={(event) => setPhone(event.target.value)}
                data-testid="mailing-list-test-phone"
              />
            </Field>
          ) : (
            <Field label="Тест-письмо на адрес" htmlFor="mailing-list-test-email">
              <Input
                id="mailing-list-test-email"
                type="email"
                value={email}
                placeholder="ваш@ismart.org"
                disabled={locked}
                onChange={(event) => setEmail(event.target.value)}
                data-testid="mailing-list-test-email"
              />
            </Field>
          )}
          <Button
            disabled={locked || !(sms ? phone : email).trim() || !selected || testSend.isPending}
            loading={testSend.isPending}
            data-testid="mailing-list-test-send"
            onClick={() =>
              selected &&
              testSend.mutate(
                sms
                  ? { listId, applicationId: selected.applicationId, phone: phone.trim() }
                  : { listId, applicationId: selected.applicationId, email: email.trim() },
                {
                  // The house envelope — T.Z185: the answer's payload lives in `data`.
                  onSuccess: (answer) =>
                    onNotice(`${sms ? 'Тест-SMS ушло на' : 'Тест-письмо ушло на'} ${answer.data.to}.`),
                  onError: (error) => onNotice(`Тест не ушёл: ${error.message}`),
                },
              )
            }
          >
            {sms ? 'Отправить тест-SMS' : 'Отправить тест'}
          </Button>
          <Button
            variant="primary"
            disabled={locked || sendable === 0 || sendAll.isPending}
            onClick={() => setConfirming(true)}
            data-testid="mailing-list-send-all"
          >
            Отправить всем ({sendable})
          </Button>
        </div>
      </div>
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Отправить всем?"
        description={`Будет отправлено ${sendable} ${sms ? 'сообщений' : 'писем'} — активным получателям списка, темпом движка. Кто уже получил сообщение этого списка, повторно не получает.`}
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>Отмена</Button>
            <Button
              variant="primary"
              loading={sendAll.isPending}
              data-testid="mailing-list-send-all-confirm"
              onClick={() =>
                sendAll.mutate(
                  { listId },
                  {
                    onSuccess: (answer) => {
                      setConfirming(false);
                      // T.Z185 — the fold reads the run off the envelope's `data`
                      // (ONE place, `formatSendAllNotice`); reading the keys off
                      // the envelope itself is what once announced «undefined
                      // получателей (запуск undefined)» while the letters went out.
                      onNotice(formatSendAllNotice(answer.data));
                    },
                    onError: (error) => {
                      setConfirming(false);
                      onNotice(`Не удалось запустить: ${error.message}`);
                    },
                  },
                )
              }
            >
              Отправить
            </Button>
          </>
        }
      >
        <p>
          {sms
            ? `Список: ${sendable} готовых к отправке получателей. Сообщение собирается из SMS-шаблона списка и уходит с подписью, заданной в окружении.`
            : `Список: ${sendable} готовых к отправке получателей. Письмо собирается из шаблона, закреплённого за списком, и уходит с его адреса «От кого».`}
        </p>
      </Modal>
    </section>
  );
}

/**
 * The list's OWN send status (T.Z185, the owner's «add status in next wave for
 * this mailings») — the fixed panels' chip grammar over #34.10: всего
 * получателей / принято / доставлено / ошибок / не получил, every number scoped
 * by `mailing_list_id` so no other campaign's letters borrow into it. A run
 * that is draining right now adds its progress line (#34.10 `run`), the fixed
 * panels' wording. The read is gated on the engine probe the same way the send
 * buttons are: no engine on this contour, no panel noise — just the zeros.
 */
function DeliveryStatusPanel({ listId, liveTotal, channel }: { listId: number; liveTotal: number; channel: ReturnType<typeof channelOf> }) {
  const status = useMailingListSendStatus(listId);
  const answer = status.data?.data ?? null;
  const run = answer?.run ?? null;
  const running = answer?.running ?? false;

  return (
    <section className="card mailings-delivery" data-testid="mailing-list-delivery">
      <div className="mailings-filters">
        <h2>Статус отправок</h2>
      </div>
      <section className="mailings-summary" data-testid="mailing-list-delivery-chips">
        {deliveryTiles(liveTotal, answer?.ledger, channel).map((tile) => (
          <div
            key={tile.key}
            className={`mailings-chip mailings-chip--${tile.tone}`}
            data-testid={`mailing-list-delivery-${tile.key}`}
            title={tile.hint}
          >
            <span className="mailings-chip-value">{tile.value}</span>
            <span className="mailings-chip-label">{tile.label}</span>
          </div>
        ))}
        {/* ADDITIVE v1.9.73 (T.Z211) — the short links' clicks chip: renders only
            when somebody in this list actually owns a code. */}
        {clickTiles(answer?.clicks).map((tile) => (
          <div
            key={tile.key}
            className={`mailings-chip mailings-chip--${tile.tone}`}
            data-testid={`mailing-list-delivery-${tile.key}`}
            title={tile.hint}
          >
            <span className="mailings-chip-value">{tile.value}</span>
            <span className="mailings-chip-label">{tile.label}</span>
          </div>
        ))}
      </section>
      {running && run ? (
        <p className="mailings-run-progress" data-testid="mailing-list-run-progress">
          {formatListRunProgress(run)}
        </p>
      ) : null}
      {status.isError ? (
        <p className="mailings-lists-hint" data-testid="mailing-list-delivery-error">
          Статус отправок сейчас не читается: {String(status.error)}
        </p>
      ) : null}
    </section>
  );
}

function RecipientsTable({
  listId,
  sms,
  rows,
  page,
  total,
  onPage,
}: {
  listId: number;
  sms: boolean;
  rows: MailingListRecipientDto[];
  page: number;
  total: number;
  onPage: (next: number) => void;
}) {
  const [previewId, setPreviewId] = useState<number | null>(null);
  const pages = Math.max(1, Math.ceil(total / LIST_PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * LIST_PAGE_SIZE + 1;
  const to = Math.min(page * LIST_PAGE_SIZE, total);
  // ADDITIVE v1.9.73 (T.Z211) — the click column exists only where the api sent
  // click facts (a recipient with no short-link code of this list carries no
  // `clicked` key at all): a pre-T.Z211 campaign's table keeps its six columns.
  const hasClickFacts = rows.some((row) => row.clicked !== undefined);
  return (
    <>
      <TableWrap>
        <table className="data mailings-table" data-testid="mailing-list-recipients">
          <thead>
            <tr>
              <th>ФИО</th>
              <th>{sms ? 'Телефон' : 'E-mail'}</th>
              <th>ЕПГУ / УНТИ</th>
              <th>Статус при подборе</th>
              <th>Трек</th>
              <th>Письмо</th>
              {hasClickFacts ? <th>Переход</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.applicationId}
                className="mailings-row"
                onClick={() => setPreviewId(row.applicationId)}
                data-testid="mailing-list-recipient-row"
              >
                <td className="cell-primary">{row.fullName ?? '—'}</td>
                <td className="cell-muted">{sms ? row.phone ?? '—' : row.email ?? '—'}</td>
                <td className="cell-compact cell-muted">
                  {row.pguId ?? '—'}
                  {' / '}
                  {row.untiId ?? '—'}
                </td>
                <td className="cell-compact">
                  <Badge tone={STATUS_TONE[row.statusAtResolve]}>{STATUS_LABEL[row.statusAtResolve]}</Badge>
                </td>
                <td className="cell-compact">{trackLabel(row.track, row.trackResolved)}</td>
                <td className="cell-compact">
                  <Badge
                    tone={renderReadyLabel(row).tone}
                    title={row.greetingName ? `Обращение: ${row.greetingName}` : 'Нет имени для обращения'}
                  >
                    {renderReadyLabel(row).label}
                  </Badge>
                </td>
                {hasClickFacts ? (
                  <td className="cell-compact" data-testid="mailing-list-recipient-click">
                    {row.clicked ? (
                      <Badge tone="ok" title={row.clickedAt ? `Первый переход: ${formatWhen(row.clickedAt)}` : 'Переход по короткой ссылке'}>
                        кликнул
                      </Badge>
                    ) : (
                      <span className="cell-muted">—</span>
                    )}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
      <Pagination
        meta={{ page, totalPages: pages, total, limit: LIST_PAGE_SIZE }}
        onPage={onPage}
        rangeCaption={`Показаны ${from}–${to} из ${total}`}
      />
      <PreviewModal listId={listId} applicationId={previewId} onClose={() => setPreviewId(null)} />
    </>
  );
}

/**
 * T.Z128's rule, unchanged for lists: the letter is third-party HTML — the
 * iframe is sandboxed with NO scripts and NO same-origin, so the #34.7 render
 * never gets to run anything.
 */
function PreviewModal({ listId, applicationId, onClose }: { listId: number; applicationId: number | null; onClose: () => void }) {
  const preview = useMailingListPreview(listId, applicationId);
  const letter = preview.data?.data ?? null;
  // T.Z210 — an SMS message has no markup: it renders as TEXT in a read-only
  // box, never through the sandboxed HTML frame (what the api answers in `html`
  // there is the message text verbatim).
  const sms = letter ? (letter.channel ?? 'EMAIL') === 'SMS' : false;
  return (
    <Modal open={applicationId !== null} onClose={onClose} title={sms ? 'Предпросмотр сообщения' : 'Предпросмотр письма'} wide>
      {preview.isPending || !letter ? (
        <Spinner label="Готовим предпросмотр" />
      ) : (
        <div className="mailings-preview" data-testid="mailing-list-preview">
          <p className="mailings-preview-meta">
            {sms ? 'SMS' : letter.subject} · трек {trackLabel(letter.track, letter.trackResolved)} · ссылка {letter.startUrl}
          </p>
          {sms ? (
            <Textarea
              rows={6}
              value={letter.html}
              readOnly
              aria-label="Текст сообщения"
              data-testid="mailing-list-preview-text"
            />
          ) : (
            <iframe
              className="mailings-preview-frame"
              title="Предпросмотр письма"
              sandbox=""
              srcDoc={letter.html}
              data-testid="mailing-list-preview-frame"
            />
          )}
        </div>
      )}
    </Modal>
  );
}
